import { AspectRatio, Image } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PlayIcon } from '~/components/icons';
import { PIXEL_IMAGE, YT_ASPECT_RATIO } from '~/constants';
import { useInViewport } from '~/hooks/useInViewport';
import theme from '~/styles/theme';
import { ytContentType } from '~/types';

const propTypes = {
  content: ytContentType.isRequired,
  id: PropTypes.string.isRequired,
  iframeAttributes: PropTypes.shape({}),
};

const addPrefetch = (kind, url, as) => {
  const linkElem = document.createElement('link');
  linkElem.rel = kind;
  linkElem.href = url;
  if (as) linkElem.as = as;
  linkElem.crossOrigin = 'true';
  document.head.append(linkElem);
};

export const LazyYoutubeVideo = ({
  content,
  id,
  iframeAttributes = {},
  ...aspectRatioProps
}) => {
  const containerRef = useRef();
  const { isInView } = useInViewport(containerRef);
  const posterSrc = useMemo(
    () => (isInView ? content?.thumbnails?.medium?.url : PIXEL_IMAGE),
    [isInView, content],
  );
  const [isActiveIframe, setIsActiveIframe] = useState(false);
  const [showPlayIcon, setShowPlayIcon] = useState(true);
  const youtubePlayer = useRef();
  const [currentPlayerState, setCurrentPlayerState] = useState();

  const onPlayerReady = useCallback(
    ({ target }) => {
      const iframe = target.getIframe();
      youtubePlayer.current = target;
      iframe.setAttribute('allow', 'fullscreen');
      iframe.setAttribute('title', content.title);
      for (const [attribute, value] of Object.entries(iframeAttributes)) {
        iframe.setAttribute(attribute, value);
      }
      target.playVideo();
    },
    [content.title, iframeAttributes],
  );

  const onPlayerStateChange = useCallback(
    ({ data }) => {
      setCurrentPlayerState(data);
      if (data === window.YT.PlayerState.ENDED) {
        youtubePlayer.current.seekTo(0);
        youtubePlayer.current.pauseVideo();
        return;
      }
      setShowPlayIcon(
        [window.YT.PlayerState.PAUSED, window.YT.PlayerState.ENDED].includes(
          data,
        ),
      );
    },
    [setShowPlayIcon, youtubePlayer],
  );

  const addIframe = useCallback(() => {
    new window.YT.Player(`${id}-player`, {
      videoId: content.ytVideoId,
      playerVars: {
        autoplay: 1,
        controls: 0,
        enablejsapi: 1,
        iv_load_policy: 3,
        modestbranding: 1,
        rel: 0,
        showinfo: 0,
        widget_referrer: window.location.href,
      },
      events: {
        onReady: onPlayerReady,
        onStateChange: onPlayerStateChange,
      },
    });

    setIsActiveIframe(true);
  }, [content.ytVideoId, id, onPlayerReady, onPlayerStateChange]);

  const loadIframeAPI = () => {
    const scriptId = 'youtube-iframe-api';
    const scriptAlreadyInserted = !!document.querySelector(`#${scriptId}`);
    if (!scriptAlreadyInserted) {
      const tag = document.createElement('script');
      tag.id = scriptId;
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      if (firstScriptTag) {
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      } else {
        document.body.appendChild(tag);
      }
    }
  };

  useEffect(() => {
    addPrefetch('preconnect', 'https://www.youtube.com');
    // The botguard script is fetched off from google.com
    addPrefetch('preconnect', 'https://www.google.com');
    // Not certain if these ad related domains are in the critical path. Could verify with domain-specific throttling.
    addPrefetch('preconnect', 'https://googleads.g.doubleclick.net');
    addPrefetch('preconnect', 'https://static.doubleclick.net');

    loadIframeAPI();
  }, []);

  const handleClick = useCallback(() => {
    if (!window.YT) return;
    if (!isActiveIframe) {
      window.YT.ready(addIframe);
    } else {
      switch (currentPlayerState) {
        case window.YT.PlayerState.PLAYING:
          youtubePlayer.current.pauseVideo();
          break;
        case window.YT.PlayerState.PAUSED:
          youtubePlayer.current.playVideo();
          break;
        default:
          break;
      }
    }
  }, [addIframe, currentPlayerState, isActiveIframe, youtubePlayer]);

  useEffect(() => {
    const containerElement = containerRef?.current;
    if (containerElement) {
      containerElement.addEventListener('click', handleClick);
    }

    return () => {
      containerElement?.removeEventListener('click', handleClick);
    };
  }, [containerRef, handleClick]);

  return (
    <AspectRatio
      id={id}
      onClick={handleClick}
      overflow="hidden"
      ratio={YT_ASPECT_RATIO}
      ref={containerRef}
      {...aspectRatioProps}
    >
      <>
        <Image
          id={`${id}-player`}
          src={posterSrc}
          borderStyle="none"
          fontSize={0} // Hide Alt Text on image load on Firefox
          height="100%"
          loading="lazy"
          objectFit="cover"
          width="100%"
          {...theme.lazyLoad.Loading}
        />
        {showPlayIcon && (
          <PlayIcon
            aria-label="Play video"
            color="white"
            margin="auto"
            position="absolute"
            /*
              Note: this should work with `boxSize="34px"` for Icon,
              but when inside `AspectRatio`, it forces height/with to 100%
              whenever the direct children doesn't have `style` tag.
              See: https://github.com/chakra-ui/chakra-ui/blob/main/packages/layout/src/aspect-ratio.tsx
            */
            style={{ height: '34px', width: '34px' }}
          />
        )}
      </>
    </AspectRatio>
  );
};

LazyYoutubeVideo.propTypes = propTypes;
